'use client'
import * as React from 'react'
import { Badge, BadgeProps } from '@/ui/badge'
import { Button, ButtonProps } from '@/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/shared/lib/classnames'
import { Label } from '../../label'
import { CardDescription } from '../../card'

export type OptionType = Record<string, any>

export interface MultipleSelectProps<T extends string> {
  options: OptionType[]
  defaultSelected?: OptionType[]
  selected?: OptionType[]
  onChange?: React.Dispatch<React.SetStateAction<OptionType[]>>
  idField?: T
  valueField?: T
  labelField?: string
  className?: string
  defaultLabel?: string
  badgeProps?: BadgeProps
  badgeButtonProps?: ButtonProps
}

const MultipleSelect = React.forwardRef<HTMLButtonElement, MultipleSelectProps<string>>(
  (
    {
      options,
      defaultSelected,
      selected: values,
      onChange,
      idField = 'id',
      valueField = 'value',
      labelField = 'label',
      className,
      defaultLabel = 'Выбрать',
      badgeProps,
      badgeButtonProps,
      ...props
    },
    ref,
  ) => {
    const [selected, setSelected] = useState<OptionType[]>([])
    const [open, setOpen] = useState(false)
    const handleUnselect = (item: OptionType) => {
      const selectedItems = selected.filter((i) => i[idField] !== item[idField])
      setSelected(selectedItems)
      onChange && onChange(selectedItems)
    }

    const handleSelect = (option: OptionType) => {
      const selectedItems = selected?.some((item: OptionType) => item[idField] === option[idField])
        ? selected.filter((item) => item[idField] !== option[idField])
        : [...selected, option]
      setSelected(selectedItems)
      onChange && onChange(selectedItems)
    }

    React.useEffect(() => {
      if (defaultSelected && defaultSelected?.length > 0) {
        setSelected(defaultSelected)
      }
    }, [defaultSelected])

    React.useEffect(() => {
      if (values) setSelected(values)
      if (values === undefined) setSelected([])
    }, [values])

    return (
      <Popover open={open} onOpenChange={setOpen} {...props}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            {...badgeButtonProps}
            role="combobox"
            ref={ref}
            aria-expanded={open}
            className={`w-full justify-between ${
              selected?.length > 1 ? 'h-[fit-content]' : 'h-10'
            } ${badgeButtonProps?.className || ''}`}
            onClick={() => setOpen(!open)}
          >
            <div className="flex gap-1 flex-wrap max-w-multiselect overflow-x-hidden">
              {selected?.map((item, index) => (
                <Badge
                  variant="default"
                  {...badgeProps}
                  className={cn('flex gap-x-2 max-w-full justify-between items-center', badgeProps?.className)}
                  key={index}
                  onClick={() => handleUnselect(item)}
                >
                  <div className="w-11.5/12 truncate">{item[labelField]}</div>
                  <div
                    className="ml-1 ring-offset-background rounded-full outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(item)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={() => handleUnselect(item)}
                  >
                    <X className="h-3 w-3 text-primary-foreground" />
                  </div>
                </Badge>
              ))}
              {selected?.length < 1 && <Label className="font-normal">{defaultLabel}</Label>}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="relative z-999999999999999! w-full">
          <Command className={cn('w-full', className)}>
            <CommandInput placeholder="Поиск..." />
            <CommandEmpty>Ничего не найдено</CommandEmpty>
            <CommandGroup className="h-32 overflow-auto">
              {options.length < 1 && <CardDescription className="text-center mt-10">Данных нет</CardDescription>}
              {options.map((option, index) => (
                <CommandItem
                  key={index}
                  onSelect={() => {
                    handleSelect(option)
                    setOpen(true)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selected?.some((item) => item[idField] === option[idField]) ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {option[labelField]}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

MultipleSelect.displayName = 'MultipleSelect'
export { MultipleSelect }
